import { useT } from "@transifex/react";
import { ProhibitIcon } from "@phosphor-icons/react";
import type { SchoolClass } from "p6m-p6u";
import { SchoolClassVisibilities } from "../../../enums/schoolClasses";
import { useEffect, useState } from "react";
import InlineFormError from "../../../basic/InlineFormError";
import BasePopupWrapper, { TitleColorVariants } from "../../../basic/popups/BasePopupWrapper";
import { ButtonColorVariant } from "../../teacherArea/AreaButton";
import { ClassActivityCategory } from "../../../constants/ClassActivityConstants";
import { ClassSelectionCard } from "./ClassSelectionCard";
import InlineCreateClassForm from "../../schoolClasses/InlineCreateClassForm";
import PhaseSixIcon from "../../../basic/phaseSixIcon/PhaseSixIcon";
import useBreakpoints, { Breakpoint } from "../../../hooks/useBreakpoints";
import { useRTContext } from "../../../context/RTContext";
import { createClassAndAssign } from "../../../helpers/ClassAssignment";
import type { SchoolClassChangeHandler } from "../ClassActivityCardsOverview.types";
import { useLogger } from "../../../hooks/useLogger";
import { getUniqueDefaultClassName, isPrivateSchoolClass, validateClassName } from "../../../utils/schoolClassUtils";

interface ClassAssignmentPopupProps {
    isOpen: boolean;
    onClose: () => void;
    availableSchoolClasses: SchoolClass[];
    classActivityCategory: ClassActivityCategory;
    onSchoolClassChange: SchoolClassChangeHandler;

    selectedSchoolClassId?: string;
    // Any shared test (TEST or PRACTICE mode) is reassigned via copy + delete, so its link changes.
    isShared?: boolean;
    // Shared TEST has special constraints: "no class" cannot be chosen, and reassigning wipes
    // existing answers (public class targets drop the sharing status). Shared practice has none of those.
    isSharedTest?: boolean;
}

const VISIBLE_CLASS_COUNT_MOBILE = 2;
const VISIBLE_CLASS_COUNT_DESKTOP = 4;

const titleColorMap: Record<ClassActivityCategory, TitleColorVariants> = {
    [ClassActivityCategory.TEST]: TitleColorVariants.DEFAULT,
    [ClassActivityCategory.VOCAB_TRAINING]: TitleColorVariants.SECONDARY,
};

const buttonColorMap: Record<ClassActivityCategory, ButtonColorVariant> = {
    [ClassActivityCategory.TEST]: ButtonColorVariant.DEFAULT,
    [ClassActivityCategory.VOCAB_TRAINING]: ButtonColorVariant.SECONDARY,
};

export const ClassAssignmentPopup = (props: ClassAssignmentPopupProps) => {
    const t = useT();
    const { getStyleBasedOnBreakpoint, isAboveSM } = useBreakpoints();
    const { createSchoolClass, deleteSchoolClass } = useRTContext();
    const { logEvent } = useLogger();

    const t_title = t("Assign class", { _tags: "SchoolClasses, ClassAssignment" });
    const t_subtitle = t("Select your class:", { _tags: "SchoolClasses, ClassAssignment" });
    const t_newClass = t("Create new class", { _tags: "SchoolClasses, ClassAssignment" });
    const t_newClassSubtitle = t("Public or Private", { _tags: "SchoolClasses, ClassAssignment" });
    const t_noClass = t("Select no class", { _tags: "SchoolClasses, ClassAssignment" });
    const t_removeClass = t("Remove class", { _tags: "SchoolClasses, ClassAssignment" });
    const t_public = t("Public", { _tags: "SchoolClasses, ClassAssignment" });
    const t_private = t("Private", { _tags: "SchoolClasses, ClassAssignment" });
    const t_cancel = t("Cancel", { _tags: "SchoolClasses, ClassAssignment" });
    const t_assign = t("Assign", { _tags: "SchoolClasses, ClassAssignment" });
    const t_showMore = t("show more", { _tags: "SchoolClasses, ClassAssignment" });
    const t_showLess = t("show less", { _tags: "SchoolClasses, ClassAssignment" });
    const t_defaultName = t("My newest class", { _tags: "SchoolClasses" });
    const t_warningShared = t(
        "Warning: To assign already shared tests to a class, previous answers and evaluations must be deleted.",
        { _tags: "SchoolClasses, ClassAssignment" }
    );
    const t_linkChanges = t("The link to this activity will change due to the assignment.", {
        _tags: "SchoolClasses, ClassAssignment",
    });

    const [selectedId, setSelectedId] = useState<string | null>(props.selectedSchoolClassId ?? null);
    const [isExpanded, setIsExpanded] = useState(false);
    const [isCreatingMode, setIsCreatingMode] = useState(false);
    const [createName, setCreateName] = useState("");
    const [createVisibility, setCreateVisibility] = useState<SchoolClassVisibilities>(SchoolClassVisibilities.PUBLIC);
    const [createHasConsent, setCreateHasConsent] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (props.isOpen) {
            setSelectedId(props.selectedSchoolClassId ?? null);
            setIsExpanded(false);
            setIsCreatingMode(false);
            setIsSubmitting(false);
            setCreateName("");
            setCreateVisibility(SchoolClassVisibilities.PUBLIC);
            setCreateHasConsent(false);
        }
    }, [props.isOpen, props.selectedSchoolClassId]);

    const existingClassNames = props.availableSchoolClasses.map((c) => c.name);
    const isCreatePrivate = isPrivateSchoolClass(createVisibility);
    const createIsValid =
        createName.trim().length > 0 &&
        validateClassName(createName, existingClassNames) === null &&
        (!isCreatePrivate || createHasConsent);

    const hasSelectionChanged = (selectedId ?? null) !== (props.selectedSchoolClassId ?? null);

    const isConfirmDisabled =
        isSubmitting || (isCreatingMode && !createIsValid) || (!isCreatingMode && !hasSelectionChanged);

    const findClassVisibility = (schoolClassId?: string | null) =>
        props.availableSchoolClasses.find((c) => c.token === schoolClassId)?.classVisibility;

    // A shared activity is reassigned via copy + delete, so re-running the change handler cannot verify
    // the created class is empty: it recreates the test under a new id (or, with the old id already gone,
    // just fails a lookup). Resolving false skips the rollback delete — the orphan class then blocks the
    // typed name via the duplicate check until renamed, but a cascade-deleted copy would be unrecoverable.
    const detachActivity = () => {
        if (props.isShared) return false;
        return props.onSchoolClassChange(
            props.selectedSchoolClassId ?? null,
            findClassVisibility(props.selectedSchoolClassId)
        );
    };

    // Resolves whether the popup may close. A failed assignment keeps it open so the teacher can retry
    // or cancel; failing requests surface their own warning modal in the assigning context.
    const commitAssignment = async (): Promise<boolean> => {
        try {
            if (isCreatingMode) {
                return await createClassAndAssign({
                    name: createName.trim(),
                    visibility: createVisibility,
                    createSchoolClass,
                    assignSchoolClass: (schoolClassId) => props.onSchoolClassChange(schoolClassId, createVisibility),
                    detachActivity,
                    // Not silenced: a failed rollback leaves an orphan class blocking the typed name via
                    // the duplicate check, so the context's delete-error modal is the teacher's only clue.
                    deleteSchoolClass,
                });
            }
            return await props.onSchoolClassChange(selectedId ?? null, findClassVisibility(selectedId));
        } catch (e) {
            // Safety net for assignment paths that reject rather than resolve false.
            console.error("Failed to assign the class to the activity", e);
            return false;
        }
    };

    // commitAssignment cannot reject (catch-all above), so no finally is needed around the reset.
    const handleConfirm = async () => {
        if (isConfirmDisabled) return;

        setIsSubmitting(true);
        const isAssigned = await commitAssignment();
        setIsSubmitting(false);

        if (isAssigned) props.onClose();
    };

    const getClassVisibilityLabel = (classVisibility: SchoolClassVisibilities) => {
        return classVisibility === SchoolClassVisibilities.PUBLIC ? t_public : t_private;
    };

    const handleSelectNoClass = () => setSelectedId(null);

    const handleSelectClass = (classId: string) => {
        setSelectedId((prev) => (prev === classId && !props.isSharedTest ? null : classId));
    };

    const handleToggleExpanded = () => setIsExpanded((prev) => !prev);

    const handleOpenCreateMode = () => {
        logEvent("started class creation", { source: "class_assignment_popup" });
        setCreateName(getUniqueDefaultClassName(t_defaultName, existingClassNames));
        setCreateVisibility(SchoolClassVisibilities.PUBLIC);
        setCreateHasConsent(false);
        setIsCreatingMode(true);
    };
    const handleCloseCreateMode = () => setIsCreatingMode(false);

    const handleVisibilityChange = (next: SchoolClassVisibilities) => {
        setCreateVisibility(next);
        if (next === SchoolClassVisibilities.PUBLIC) setCreateHasConsent(false);
    };

    const sortedClasses = [...props.availableSchoolClasses].sort((a, b) => {
        if (a.token === props.selectedSchoolClassId) return -1;
        if (b.token === props.selectedSchoolClassId) return 1;
        return b.dateInserted - a.dateInserted;
    });

    const baseVisibleClassCount = isAboveSM ? VISIBLE_CLASS_COUNT_DESKTOP : VISIBLE_CLASS_COUNT_MOBILE;
    // Shared tests hide the "no class" card, freeing one slot for an extra class.
    const visibleClassCount = props.isSharedTest ? baseVisibleClassCount + 1 : baseVisibleClassCount;
    const hasMoreClasses = sortedClasses.length > visibleClassCount;
    const visibleClasses = isExpanded ? sortedClasses : sortedClasses.slice(0, visibleClassCount);

    const renderCreateNewClassCard = () => (
        <ClassSelectionCard
            data-testid="class-selection-create-class"
            title={t_newClass}
            subtitle={`(${t_newClassSubtitle})`}
            icon="add"
            isSelected={false}
            isDashed
            isDisabled={false}
            classActivityCategory={props.classActivityCategory}
            onClick={handleOpenCreateMode}
        />
    );

    const renderNoClassCard = () => (
        <ClassSelectionCard
            data-testid="class-selection-no-class"
            title={t_noClass}
            subtitle={`(${t_removeClass})`}
            phosphorIcon={ProhibitIcon}
            isSelected={selectedId === null}
            isDashed={false}
            isDisabled={false}
            classActivityCategory={props.classActivityCategory}
            onClick={handleSelectNoClass}
        />
    );

    const renderSchoolClassCards = () =>
        visibleClasses.map((schoolClass) => (
            <ClassSelectionCard
                key={schoolClass.token}
                data-testid={`class-selection-card-${schoolClass.name}`}
                title={schoolClass.name}
                subtitle={getClassVisibilityLabel(schoolClass.classVisibility)}
                icon="group"
                isSelected={selectedId === schoolClass.token}
                isDashed={false}
                isDisabled={false}
                classActivityCategory={props.classActivityCategory}
                onClick={() => handleSelectClass(schoolClass.token)}
            />
        ));

    const renderShowMoreToggle = () => {
        if (!hasMoreClasses) return null;

        return (
            <button
                type="button"
                data-testid="class-selection-show-more"
                onClick={handleToggleExpanded}
                className={`flex items-center justify-center gap-xs text-l text-p6-grey-dark cursor-pointer ${getStyleBasedOnBreakpoint(
                    Breakpoint.SM,
                    "",
                    "pb-s"
                )}`}
            >
                <span>{isExpanded ? t_showLess : t_showMore}</span>
                <PhaseSixIcon
                    name="chevron-down"
                    className={`text-[30px] transition-transform ${isExpanded ? "rotate-180" : ""}`}
                />
            </button>
        );
    };

    const renderInfoRow = (text: string) => (
        <div className="flex gap-xs items-start">
            <PhaseSixIcon
                name="info"
                className="text-xl shrink-0 text-p6-grey-dark"
            />
            <span className="text-m text-p6-grey-dark">{text}</span>
        </div>
    );

    const renderHints = () => {
        if (!isCreatingMode && !hasSelectionChanged) return null;
        return (
            <div className="flex flex-col gap-xs">
                {props.isSharedTest && (
                    <InlineFormError data-testid="class-assignment-shared-warning">{t_warningShared}</InlineFormError>
                )}
                {props.isShared &&
                    props.classActivityCategory === ClassActivityCategory.TEST &&
                    renderInfoRow(t_linkChanges)}
            </div>
        );
    };

    return (
        <BasePopupWrapper
            isOpen={props.isOpen}
            onClose={props.onClose}
            title={t_title}
            titleColor={titleColorMap[props.classActivityCategory]}
            primaryButton={{
                text: t_assign,
                onClick: handleConfirm,
                colorVariant: buttonColorMap[props.classActivityCategory],
                className: "min-w-[180px]",
                disabled: isConfirmDisabled,
            }}
            secondaryButton={{
                text: t_cancel,
                onClick: props.onClose,
                className: "min-w-[180px]",
            }}
        >
            <div className="flex flex-col gap-s">
                <p className="text-l text-p6-grey-dark">{t_subtitle}</p>

                {isCreatingMode ? (
                    <InlineCreateClassForm
                        name={createName}
                        hasConsent={createHasConsent}
                        existingClassNames={existingClassNames}
                        onNameChange={setCreateName}
                        onConsentToggle={() => setCreateHasConsent((prev) => !prev)}
                        onClose={handleCloseCreateMode}
                        onCollapse={handleCloseCreateMode}
                        visibility={createVisibility}
                        onVisibilityChange={handleVisibilityChange}
                        classActivityCategory={props.classActivityCategory}
                        isSubmitting={isSubmitting}
                    />
                ) : (
                    <>
                        <div
                            className={`grid gap-s ${getStyleBasedOnBreakpoint(
                                Breakpoint.SM,
                                "grid-cols-2",
                                "grid-cols-1"
                            )}`}
                        >
                            {renderCreateNewClassCard()}
                            {!props.isSharedTest && renderNoClassCard()}
                            {renderSchoolClassCards()}
                        </div>
                        {renderShowMoreToggle()}
                    </>
                )}

                {renderHints()}
            </div>
        </BasePopupWrapper>
    );
};
